"use client";

import React from "react";
import { Award, LucideIcon } from "lucide-react";
import { cn } from "@utils/cn";

export interface BadgeProps {
  /** Badge text content */
  children: React.ReactNode;
  /** Optional icon component (defaults to Award, set to null to hide) */
  icon?: LucideIcon | null;
  /** Badge variant style */
  variant?: "solid" | "transparent" | "light" | "white";
  /** Badge size */
  size?: "sm" | "md" | "lg";
  /** Custom className */
  className?: string;
  /** Show backdrop blur effect */
  backdropBlur?: boolean;
  /** Show border (default: false) */
  showBorder?: boolean;
  /** Custom icon className */
  iconClassName?: string;
  /** Custom text className */
  textClassName?: string;
}

/**
 * Badge component for displaying labels, tags, or status indicators
 * 
 * @example
 * <Badge variant="solid">Expertise</Badge>
 * <Badge variant="transparent" icon={Award}>Featured</Badge>
 */
export const Badge: React.FC<BadgeProps> = ({
  children,
  icon,
  variant = "solid",
  size = "md",
  className,
  backdropBlur = false,
  showBorder = false,
  iconClassName,
  textClassName,
}) => {
  // Default to Award icon if icon is undefined, but allow null to hide it
  const Icon = icon === undefined ? Award : icon;
  // Size classes
  const sizeClasses = {
    sm: "px-3 py-1",
    md: "px-4 py-2",
    lg: "px-5 py-2.5",
  };

  // Icon size classes
  const iconSizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  // Variant classes
  const variantClasses = {
    solid: "bg-abis-accent",
    transparent: "bg-abis-accent/10",
    light: "bg-abis-accent/20",
    white: "bg-white/10",
  };

  // Text color classes
  const textColorClasses = {
    solid: "text-white",
    transparent: "text-orange-700",
    light: "text-abis-accent",
    white: "text-white/90",
  };

  // Icon color classes
  const iconColorClasses = {
    solid: "text-white",
    transparent: "text-orange-700",
    light: "text-abis-accent",
    white: "text-white",
  };

  // Border classes (only if showBorder is true)
  const getBorderClass = () => {
    if (!showBorder) return "";
    switch (variant) {
      case "solid":
        return "border border-white";
      case "transparent":
        return "border border-abis-accent/20";
      case "light":
        return "border border-abis-accent/30";
      case "white":
        return "border border-white/20";
      default:
        return "";
    }
  };

  const baseClasses = cn(
    "inline-flex items-center rounded-full font-semibold text-xs uppercase tracking-wide",
    sizeClasses[size],
    variantClasses[variant],
    backdropBlur && "backdrop-blur-sm",
    getBorderClass(),
    className
  );

  const iconClasses = cn(
    iconSizeClasses[size],
    iconColorClasses[variant],
    "mr-2",
    iconClassName
  );

  const textClasses = cn(textColorClasses[variant], textClassName);

  return (
    <div className={baseClasses}>
      {Icon !== null && <Icon className={iconClasses} aria-hidden="true" />}
      <span className={textClasses}>{children}</span>
    </div>
  );
};

export default Badge;

